import {
  DEFAULT_WORKER_WINDOW_SIZE,
  HEADER_HEIGHT,
  PHONE_BEZEL_WIDTH,
  STALE_CLEANUP_MS,
  WORKPLACE_WINDOW_HEIGHT_RATIO,
  WORKPLACE_WINDOW_WIDTH_RATIO,
} from "../constants";
import type { ImpersonationSession, ImpersonationSessionType, PersistedSession } from "../types";

const STORAGE_KEY = "impersonation-sessions";
const BALLOON_WIDTH = 220;
const BALLOON_HEIGHT = 36;
const BALLOON_BOTTOM_OFFSET = 80;
const BALLOON_SPACING = 50;
const BALLOON_RIGHT_OFFSET = 20;

let sessionCounter = 0;
export let zIndexCounter = 0;

export function nextZIndex(): number {
  return zIndexCounter++;
}

export function generateSessionId(): string {
  sessionCounter += 1;
  return `impersonation-${Date.now()}-${sessionCounter}`;
}

export function getInitialBalloonPosition(index: number): { x: number; y: number } {
  return {
    x: window.innerWidth - BALLOON_WIDTH - BALLOON_RIGHT_OFFSET,
    y: window.innerHeight - BALLOON_BOTTOM_OFFSET - index * BALLOON_SPACING,
  };
}

export function getDefaultWindowSize(sessionType: ImpersonationSessionType): {
  width: number;
  height: number;
} {
  if (sessionType === "worker") {
    return { ...DEFAULT_WORKER_WINDOW_SIZE };
  }

  return {
    width: Math.round(window.innerWidth * WORKPLACE_WINDOW_WIDTH_RATIO),
    height: Math.round(window.innerHeight * WORKPLACE_WINDOW_HEIGHT_RATIO),
  };
}

export function getInitialWindowPosition(
  sessionCount: number,
  windowSize: { width: number; height: number }
): { x: number; y: number } {
  const offset = sessionCount * 30;
  return {
    x: Math.max(50, window.innerWidth / 2 - windowSize.width / 2 + offset),
    y: Math.max(50, 100 + offset),
  };
}

function getBezelWidth(): number {
  return PHONE_BEZEL_WIDTH;
}

export function clampWindowPosition(
  session: ImpersonationSession,
  viewportWidth: number,
  viewportHeight: number
): { x: number; y: number } {
  const bezel = getBezelWidth();
  const totalWidth = session.size.width + bezel * 2;
  const totalHeight = session.size.height + bezel * 2 + HEADER_HEIGHT;
  return {
    x: Math.max(0, Math.min(session.position.x, viewportWidth - totalWidth)),
    y: Math.max(0, Math.min(session.position.y, viewportHeight - totalHeight)),
  };
}

export function clampBalloonPosition(
  balloonPosition: { x: number; y: number },
  viewportWidth: number,
  viewportHeight: number
): { x: number; y: number } {
  return {
    x: Math.max(0, Math.min(balloonPosition.x, viewportWidth - BALLOON_WIDTH)),
    y: Math.max(0, Math.min(balloonPosition.y, viewportHeight - BALLOON_HEIGHT)),
  };
}

export function persistSessions(sessions: ImpersonationSession[]): void {
  const toPersist: PersistedSession[] = sessions.map((session) => ({
    id: session.id,
    sessionType: session.sessionType,
    targetId: session.targetId,
    targetName: session.targetName,
    cognitoUsername: session.cognitoUsername,
    url: session.url,
    startedAt: session.startedAt,
    expiresAt: session.expiresAt,
  }));

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toPersist));
  } catch {
    // localStorage may be full or disabled — silently ignore
  }
}

/**
 * Legacy persisted sessions may have `workerId`/`workerName` instead of
 * `targetId`/`targetName` and no `sessionType`. This interface captures those
 * old shapes so we can migrate them on load.
 */
interface LegacyPersistedSession {
  id: string;
  sessionType?: ImpersonationSessionType;
  targetId?: string;
  targetName?: string;
  workerId?: string;
  workerName?: string;
  cognitoUsername: string;
  url: string;
  startedAt: number;
  expiresAt?: number;
}

export function loadPersistedSessions(): ImpersonationSession[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw) as LegacyPersistedSession[];
    const now = Date.now();

    const valid = parsed
      .filter((session) => now - session.startedAt < STALE_CLEANUP_MS)
      .reduce<
        Array<{
          sessionType: ImpersonationSessionType;
          targetId: string;
          targetName: string;
          session: LegacyPersistedSession;
        }>
      >((accumulator, session) => {
        const sessionType: ImpersonationSessionType = session.sessionType ?? "worker";
        const targetId = session.targetId ?? session.workerId;
        const targetName = session.targetName ?? session.workerName;
        if (!targetId || !targetName) {
          return accumulator;
        }
        try {
          new URL(session.url);
        } catch {
          return accumulator;
        }
        return [...accumulator, { sessionType, targetId, targetName, session }];
      }, []);

    return valid.map(({ sessionType, targetId, targetName, session }, index) => {
      const size = getDefaultWindowSize(sessionType);
      return {
        id: session.id,
        sessionType,
        targetId,
        targetName,
        cognitoUsername: session.cognitoUsername,
        url: session.url,
        startedAt: session.startedAt,
        expiresAt: session.expiresAt,
        isMinimized: true,
        isActive: false,
        position: getInitialWindowPosition(index, size),
        size,
        balloonPosition: getInitialBalloonPosition(index),
        zIndex: nextZIndex(),
      };
    });
  } catch {
    return [];
  }
}
